import React, {
  BaseSyntheticEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Alert, LinkButton } from '@pmi/dsm-react';
import IconDanger from '@pmi/dsm-react/dist/components/icons/16/CircleRemoveIcon';
import {
  AlertErrorSolidIcon,
  AlertInfoSolidIcon,
} from '@pmi/dsm-react/dist/components/icons';
import TagsInput from 'react-tagsinput';
import { CircleInfoIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { emailRegex, normalizeEmail, singleEmailRegex } from '../../helpers';
import { toastWithAnalytics } from '../../analytics';
import {
  useInviteUsersMutation,
  useSummarizeInviteesMutation,
} from './apiSlice';
import { accountSelector } from '../../store/shared/accountSlice';
import styles from './wizard.module.scss';
import { ProductApiModel } from '../../models/ProductModel';
import AgreementSelectedProducts from './AgreementSelectedProducts';
import { AgreementApiModel } from '../../models/DealModel';

import 'react-tagsinput/react-tagsinput.css';
import { mapPaymentArrangementText } from '../../constants';
import printableZoneRestrictionLabel from '../../utils/printableZoneRestrictionLabel';
import useUser from '../../hooks/useUser';
import useDebounce from '../../hooks/useDebounce';
import RenderHtml from '../RenderHtml';
import { CsvInvitees } from './WizardContainer';

interface EmailFormProps {
  products: ProductApiModel[];
  deal: AgreementApiModel;
  emailTags: string[];
  setEmailTags: Dispatch<SetStateAction<string[]>>;
  onSuccess: () => void;
}

export default function EmailForm({
  products,
  deal,
  emailTags = [],
  setEmailTags,
  onSuccess,
}: EmailFormProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const [summarizeInvitees] = useSummarizeInviteesMutation();
  const [inviteUsers, invitedUsers] = useInviteUsersMutation();
  const [csvInvitees, setCsvInvitees] = useState<CsvInvitees>({
    valid: [],
    invalid: [],
  });
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState<boolean>();
  const fileUploadInputRef = useRef(null);
  const [productAvailabilityError, setProductAvailabilityError] = useState<
    ProductApiModel[]
  >([]);
  const [emailInputValue, setEmailInputValue] = useState<string>('');
  const user = useUser();
  const deBouncedEmailTags = useDebounce(emailInputValue, 100).toLowerCase();
  const [emailTagInputEl, setEmailTagInputEl] =
    useState<HTMLInputElement | null>(null);
  const isQuantityRemainingOK = () => {
    setProductAvailabilityError([]);
    if (!deal.isPayByUser) {
      const errors = products
        .map((product) => {
          if (product.quantityRemaining < emailTags.length) {
            return product;
          }
          return null;
        })
        .filter((err) => err);
      if (errors.length > 0) {
        setProductAvailabilityError(errors);
        return false;
      }
    }
    return true;
  };

  const onProceed = async () => {
    if (!isQuantityRemainingOK()) {
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    inviteUsers({
      accountId: currentAccountId,
      dealId: deal.id,
      emails: emailTags,
      currentUserId: user.id,
      products: products.map((p) => p.accountProductId),
      domains: [],
    })
      .unwrap()
      .then(() => {
        invitedUsers.reset();
        onSuccess();
        setSubmitting(false);
      })
      .catch(() => {
        setSubmitting(false);
      });

    setSubmitting(true);
  };

  const showEmails = (emails: string[]) => {
    const firstEmails = emails.slice(0, 10);
    const restEmailsNum = emails.length - 10;
    return `${firstEmails.join(', ')}${
      restEmailsNum > 0 ? `... (plus ${restEmailsNum} more)` : ''
    }`;
  };
  const listEmails = (emails: string[]) => {
    const firstEmails = emails.slice(0, 10);
    const restEmailsNum = emails.length - 10;
    return (
      <ul className="ms-3">
        {firstEmails.map((email) => (
          <li>{email}</li>
        ))}
        {restEmailsNum > 0 && <li>and {restEmailsNum} more</li>}
      </ul>
    );
  };

  const downloadTemplate = () => {
    const content = `\
"** Using column A, enter one email address per row starting in row 5."
** Up to a maximum of 1000 emails can be uploaded at a time.
** Do not enter any text into any other column or your upload will fail!
************************************
`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = 'Agreement_UserInvite_Template.csv';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    setTimeout(() => {
      URL.revokeObjectURL(downloadLink.href);
      document.body.removeChild(downloadLink);
    }, 100);
  };

  const onTagsInputChange = (tags: string[]) => {
    const cleaned = new Set<string>(
      tags
        .flatMap((tag) => tag.split(/[,;\s]+/))
        .map(normalizeEmail)
        .filter((email) => email.length > 0 && singleEmailRegex.test(email)),
    );
    if (cleaned.size === 0) {
      // make sure to clear the csv invitees if user deletes all tags, to avoid confusion on the source of truth for the emails list
      setCsvInvitees({
        valid: [],
        invalid: [],
      });
    }
    setEmailTags([...cleaned]);
  };

  const doTextBoxWidth = (el: HTMLInputElement) => {
    if (el.value) {
      const aw = el.value.length / 2 + 5;
      // eslint-disable-next-line no-param-reassign
      el.style.minWidth =
        el?.value.length > 0 ? `calc(40% + ${aw < 60 ? aw : 60}%)` : '40%';
    }
  };
  const onTagsInputKeyUp = (e: React.SyntheticEvent) => {
    const el: HTMLInputElement = e?.target as HTMLInputElement;
    if (el) {
      setEmailInputValue(el?.value);
      setEmailTagInputEl(el);
      doTextBoxWidth(el);
    }
  };

  const onTagsInputPasteValues = (data: string) =>
    data
      .replaceAll(',', ' ')
      .replaceAll('\n', ' ')
      .split(' ')
      .map((d) => d.trim());

  const onTemplateUpload = (e: BaseSyntheticEvent) => {
    const fileReader = new FileReader();
    const file = e.target.files[0] as File;
    fileReader.onload = async () => {
      if (file.type !== 'text/csv') {
        toastWithAnalytics(
          `${file.type.toString()} is not allowed. The supported template formats is .csv only`,
          'danger',
        );
      } else {
        try {
          setUploadError(undefined);
          setSubmitting(true);
          summarizeInvitees({
            accountId: currentAccountId,
            dealId: deal.id,
            type: 'emailInvite',
            body: {
              dealUserRequestJson: '{}',
              csvFile: file,
            },
          })
            .unwrap()
            .then((data) => {
              setCsvInvitees({
                valid: data.validCsvEmails,
                invalid: data.invalidCsvEmails,
                fileName: file.name,
              });
              setSubmitting(false);
              if (data.validCsvEmails?.length > 0) {
                setEmailTags((prev) => [
                  ...new Set([...prev, ...data.validCsvEmails]),
                ]);
              }
            })
            .catch(() => {
              setUploadError(
                `There was a problem with ${file.name}. Please review your file and upload it again.`,
              );
              setSubmitting(false);
            });
          e.target.value = '';
        } catch {
          // reset the input field, in case user wants to try again with the same file
          e.target.value = '';
          setUploadError(
            `There was a problem with ${file.name}. Please review your file and upload it again.`,
          );
          setSubmitting(false);
        }
      }
    };

    if (file) {
      fileReader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    if (deBouncedEmailTags && emailTagInputEl) {
      if (emailTagInputEl.value.includes(' ')) {
        emailTagInputEl.blur();
        emailTagInputEl.focus();
        doTextBoxWidth(emailTagInputEl);
      }
    }
  }, [deBouncedEmailTags]);

  return (
    <div className="d-block">
      <h3>Invite People</h3>
      <div className="d-block">
        {!!uploadError && (
          <div className="text-start mt-5">
            <Alert
              variant="danger"
              alertText={uploadError}
              icon={IconDanger}
              onClose={() => setUploadError(undefined)}
            />
          </div>
        )}
        {csvInvitees.invalid.length > 0 && csvInvitees.valid.length < 1 && (
          <div className="text-start mt-5">
            <Alert
              variant="danger"
              alertText={`No valid emails were found. The following entries found were invalid and not added: ${showEmails(
                csvInvitees.invalid,
              )}`}
              icon={IconDanger}
            />
          </div>
        )}
        {csvInvitees.invalid.length > 0 && csvInvitees.valid.length > 0 && (
          <div className={`${styles.infoAlert} text-start mt-5 items-start`}>
            <AlertInfoSolidIcon
              fontSize="sm"
              className="mx-2 align-items-start"
            />
            <div className="ms-2">
              {csvInvitees.fileName} was uploaded successfully and included{' '}
              <b>
                {`${csvInvitees.valid.length} email address${
                  csvInvitees.valid.length > 1 ? 'es' : ''
                }`}
              </b>
              . The file also contained the following errors:{' '}
              {listEmails(csvInvitees.invalid)} Click “Continue” to review and
              send invites to the valid email addresses, or correct the errors
              and re-upload your file.
            </div>
          </div>
        )}
        {csvInvitees.invalid.length < 1 && csvInvitees.valid.length > 0 && (
          <div className={`${styles.infoAlert} text-start mt-5 items-start`}>
            <AlertInfoSolidIcon
              fontSize="sm"
              className="mx-2 align-items-start"
            />
            <div className="ms-2">
              {csvInvitees.fileName} was uploaded successfully and included{' '}
              <b>
                {`${csvInvitees.valid.length} email address${
                  csvInvitees.valid.length > 1 ? 'es' : ''
                }`}
              </b>
              . Click “Continue” to review and send these invites.
            </div>
          </div>
        )}
        {deal.zones.length > 0 &&
          mapPaymentArrangementText(deal) === 'Pay by User' && (
            <Alert
              variant="info"
              icon={CircleInfoIcon}
              alertText={`These products are included in the selected ${mapPaymentArrangementText(
                deal,
              )} agreement and can be assigned to individuals in a ${printableZoneRestrictionLabel(
                deal,
              )}.`}
            />
          )}
        {productAvailabilityError.length > 0 && (
          <div className="text-start align-items-start">
            {productAvailabilityError.map((product) => (
              <div
                className={`${styles.dangerAlert} text-start mb-3 items-start`}
              >
                <AlertErrorSolidIcon
                  fontSize="sm"
                  className="mx-2 align-items-start"
                  color="danger"
                />
                <div className="ms-2">
                  <RenderHtml
                    html={product.name.length > 0 ? product.name : product.sku}
                    tag="span"
                  />{' '}
                  has {product.quantityRemaining} quantity. You can not add more
                  than {product.quantityRemaining} people.
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <hr className="mt-3 mb-4" />
      <div className={`${styles.inviteFormContainer} gap-5`}>
        <div className="d-block flex-grow-1">
          <div className="text-center mb-5">
            <div className="max-700">
              <div className="text-start">
                <label htmlFor="emailAddress" className="d-block mb-2">
                  Email Address
                </label>
                <TagsInput
                  value={emailTags}
                  onChange={onTagsInputChange}
                  addOnBlur
                  pasteSplit={onTagsInputPasteValues}
                  onlyUnique
                  validationRegex={emailRegex}
                  inputProps={{
                    placeholder:
                      emailTags.length > 0
                        ? 'input@email.com'
                        : 'Please enter an email address',
                    id: 'emailAddress',
                    onKeyUp: onTagsInputKeyUp,
                  }}
                  className={`rounded-1 border border-1 px-2 py-2 ${
                    styles.tagsInput
                  } ${emailTags.length > 0 ? styles.hasInputTags : ''}`}
                />
              </div>
              <div className="mb-4 small text-start text-muted">
                <div className="mt-2">
                  Include multiple, comma-separated emails to invite more than
                  one person at a time.
                </div>
                <div className="d-flex justify-content-start align-items-center">
                  <span className="me-1 d-inline-block">Or use</span>
                  <span className="d-inline-block">
                    <LinkButton
                      className={`${styles.smallLinkText} text-decoration-none p-0 small`}
                      variant="light"
                      titleText="this template"
                      onClick={() => downloadTemplate()}
                      size="xs"
                    />
                  </span>
                  <span className="d-inline-block mx-1"> and </span>
                  <span className="d-inline-block">
                    <LinkButton
                      className={`${styles.smallLinkText} text-decoration-none p-0 small`}
                      variant="light"
                      titleText="upload a .csv file."
                      onClick={() => fileUploadInputRef.current.click()}
                      size="xs"
                    />
                  </span>
                </div>
                <input
                  type="file"
                  name="account-logo"
                  className="d-none"
                  accept="text/csv"
                  ref={fileUploadInputRef}
                  onChange={onTemplateUpload}
                  data-testid="file-input"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="d-block">
          {products && products.length > 0 && (
            <AgreementSelectedProducts
              products={products}
              deal={deal}
              onClick={onProceed}
              disabled={emailTags.length === 0 || submitting}
              buttonText="Invite & Assign  Products"
              emailCount={emailTags.length}
              isInviteStep
            />
          )}
        </div>
      </div>
    </div>
  );
}
